<template>
  <div v-if="materialoCode" class="FloorPlannerPromo bg-bg-accenture mb-32 desk:mb-40">
    <div class="TextImageVideoBlock desktopMaxW-full blockPaddingTop-md blockPaddingBottom-md storyblok__outline">
      <div class="layout-container grid grid-cols-6 desk:grid-cols-12 gap-32 desk:gap-16">
        <div class="grid grid-cols-subgrid gap-8 desk:gap-16 col-span-6 desk:order-2">
          <div class="aspect-square col-span-4 order-2">
            <div class="overflow-hidden w-full h-full">
              <NuxtImg
                :src="hoverImage ? hoverImage : productItem.mainImage"
                provider="norce"
                preset="standard"
                loading="lazy"
                sizes="sm:100vw desk:448px"
                class="h-full w-full object-cover transition-all duration-[50ms]"
                style="transform: translateY(-22.1px) scale(1.2); transform-origin: center top;"
                :alt="productItem.name"
              />
            </div>
          </div>
          <div
            class="mt-32 desk:mt-64 aspect-square col-span-2"
            style="transform: translateY(22.1px);"
          >
            <NuxtImg
              v-if="hoverImage"
              :src="productItem.mainImage"
              provider="norce"
              preset="standard"
              loading="lazy"
              sizes="sm:100vw desk:237px"
              class="h-full w-full object-cover object-center"
              :alt="productItem.name"
            />
          </div>
        </div>
        <div class="grid grid-cols-subgrid col-span-6">
          <div class="col-start-1 -col-end-1 desk:col-start-2 flex items-center desk:-col-end-2">
            <div class="flex flex-col gap-12 items-start">
              <div class="type-tagline-sm">
                {{ $t('plannerPromo.tagline') }}
              </div>
              <div class="type-headline-3xl">
                {{ $t('plannerPromo.headline') }}
              </div>
              <div class="wysiwyg max-w-[40ch]">
                <div class="ExtendedWysiwyg rich-text-p-container">
                  <div class="">
                    <p>
                      {{ $t('plannerPromo.text', { productName: productItem.name }) }}
                    </p>
                  </div>
                </div>
              </div>
              <div class="flex flex-wrap gap-20 desk:gap-24 mt-12">
                <nuxt-link :to="floorplannerLink" class="btn btn--underline">{{ $t('plannerPromo.buttonText') }}</nuxt-link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ProductModel } from '#models/product';
import { ProductVariantModel } from '#models/productVariant';
import { useGlobalContentStore } from '~/store/globalContent';

const globalContent = useGlobalContentStore();

const { $manualUrl } = useNuxtApp();

const props = defineProps<{
  productItem: ProductModel;
  activeVariant: ProductVariantModel,
}>();

const materialoCode = computed(()=> {
  return props.productItem.parametricByCode('MaterialoArticleNumber');
});

const hoverImage = computed(()=> {
  const hoverImage = props.productItem.mediaSets.find(f => f.code === 'InteriorImage');
  if (hoverImage && hoverImage.media[0]) {
    return hoverImage.media[0].url;
  }
  return null;
});

/*
https://kahrs.materialo.com/?start_text=V%c3%a4lkommen+till&start_text2=K%c3%a4hrs+Floor+Planner&sid=42422&art_nr=133NCDEKB9KW240&language=se&design=3-stav&surface=Mattlack&art_name=Ek%20Ore&lng_str=sv-SE&construction=Lamellparkett&type=Floors

 &sid=42422 (verkar inte behövas)
&design=3-stav (ProductDesign)
&surface=Mattlack (ItemSurfaceTreatment)
&construction=Lamellparkett (ProductConstruction)
&type=Floors
&art_name=
 */

const floorplannerLink = computed(()=> {
  // Temp fix for DE
  if (globalContent.getCountryCode === 'de') {
    return 'https://www.kahrs.com/en/floorplanner/panel#traffic=hightraffic&color=8,6&g=Construction';
  }

  let output = `floorplanner?art_nr=${materialoCode.value?.value}&art_name=${props.productItem.name}`;

  const ProducDesign = props.productItem.parametricByCode('ProductDesign');
  // Multi value
  if (ProducDesign?.multipleValues) {
    output += `&design=${ProducDesign.multipleValues[0].name}`;
  }
  const ItemSurfaceTreatment = props.productItem.parametricByCode('ItemSurfaceTreatment');
  if (ItemSurfaceTreatment?.listValue) {
    output += `&surface=${ItemSurfaceTreatment.listValue.name}`;
  }
  const ProductConstruction = props.productItem.parametricByCode('ProductConstruction');
  if (ProductConstruction?.listValue) {
    output += `&construction=${ProductConstruction.listValue.name}`;
  }

  return $manualUrl(encodeURI(output));
});

</script>